'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/Components/ui/card'
import { Link } from '@inertiajs/react'
import { LazySvg } from '@/Components/custom/lazy-svg'
import { useTranslation } from 'react-i18next'

interface NewsCardProps {
  id: number
  className?: string
  excerpt: string
  media: string
  publishedAt: string
  title: string
}

export default function NewsCard({
  id,
  className,
  excerpt,
  media,
  publishedAt,
  title,
}: NewsCardProps) {
  const { t } = useTranslation()
  return (
    <Card className="group w-full overflow-hidden !rounded-[20px] border-none bg-card shadow-md transition-shadow duration-300 hover:shadow-lg">
      <motion.div
        className={cn('flex h-[380px] flex-col gap-[22px]', className)}
        whileHover="hover"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <CardHeader className="relative h-[200px] overflow-hidden">
          <motion.div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${media})` }}
            variants={{
              initial: { scale: 1 },
              hover: { scale: 1.2 },
            }}
            transition={{
              duration: 0.3,
            }}
          />
        </CardHeader>
        <CardContent className="flex flex-col items-start justify-center gap-4 py-0">
          <p className="text-xs text-body">{publishedAt}</p>
          <CardTitle className="line-clamp-1 text-xl font-semibold leading-tight tracking-tighter text-foreground">
            {title}
          </CardTitle>
          <p className="line-clamp-3 text-sm font-medium text-body">{excerpt}</p>
        </CardContent>
      </motion.div>
      <CardFooter className="flex justify-end">
        <Link
          prefetch
          href={`/news/${id}`}
          className="flex items-center justify-center space-x-0.5 text-caption outline-none group-hover:text-primary rtl:space-x-reverse"
        >
          <span className="text-xs font-medium rtl:font-semibold">{t('continueReading')}</span>
          <LazySvg name="/icons/arrow-right.svg" className="size-2 rtl:rotate-180" />
        </Link>
      </CardFooter>
    </Card>
  )
}
